import React, {
  useEffect,
  createContext,
  useState,
  useContext,
  useCallback,
  ReactNode,
} from "react";
import io, { Socket } from "socket.io-client";
import { useAuth } from "./auth.context";

interface BaseSocketResponse {
  type: string;
  conversationId: string;
  requestId: string;
}

interface NewTokenResponse extends BaseSocketResponse {
  type: "newtoken";
  token: string;
}

interface EndTokenResponse extends BaseSocketResponse {
  type: "endtoken";
  token: string;
  fullResponse: string;
}

interface CtxBlockResultResponse extends BaseSocketResponse {
  type: "ctxBlockResult";
  ctxBlockResult: any; // You might want to define a more specific type here
}

interface ErrorResponse extends BaseSocketResponse {
  type: "error";
  error: string;
}

export type SocketResponse =
  | NewTokenResponse
  | EndTokenResponse
  | CtxBlockResultResponse
  | ErrorResponse;

interface SocketContextType {
  socket: Socket | null;
  responses: Record<string, SocketResponse[]>;
  clearResponse: (conversationId: string) => void;
  connectionFailed: boolean;
  resetConnection: () => void;
  addEventListener: (
    eventName: string,
    callback: (event: CustomEvent) => void
  ) => void;
  removeEventListener: (
    eventName: string,
    callback: (event: CustomEvent) => void
  ) => void;
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

interface SocketProviderProps {
  children: ReactNode;
}

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [socket, setSocket] = useState<Socket | null>(null);
  const [responses, setResponses] = useState<Record<string, SocketResponse[]>>(
    {}
  );
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const [connectionFailed, setConnectionFailed] = useState(false);

  const resetConnection = useCallback(() => {
    setReconnectAttempts(0);
    setConnectionFailed(false);
    if (socket) {
      socket.close();
      setSocket(null);
    }
  }, [socket]);

  useEffect(() => {
    if (!socket && isAuthenticated && !connectionFailed) {
      const newSocket: Socket = io(
        import.meta.env.VITE_GPT_GATEWAY_SOCKET_URL,
        {
          path: import.meta.env.VITE_GPT_GATEWAY_PATH,
          withCredentials: true,
          reconnectionAttempts: 3, // Set Socket.IO's built-in reconnection limit
        }
      );

      newSocket.on("connect", () => {
        console.log("Socket connected:", newSocket.id);
        // Reset reconnect attempts on successful connection
        setReconnectAttempts(0);
        setConnectionFailed(false);

        setSocket(newSocket);
      });

      newSocket.on("disconnect", () => {
        console.error("Socket disconnected");
        // We'll handle reconnection through Socket.IO's built-in mechanism
        // and track the attempts
      });

      newSocket.on("reconnect_attempt", (attemptNumber) => {
        console.log(`Reconnection attempt ${attemptNumber} of 3`);
        setReconnectAttempts(attemptNumber);
      });

      newSocket.on("reconnect_failed", () => {
        console.error(
          "Max reconnection attempts reached. Please refresh the page."
        );
        setConnectionFailed(true);
        newSocket.disconnect();
      });

      newSocket.on("connect_error", (err: Error) => {
        console.error("Connection error:", err);
        // Socket.IO will handle reconnection attempts
      });

      newSocket.io.on("reconnect_error", (err) => {
        console.error("Reconnection error:", err);
        if (reconnectAttempts >= 3) {
          console.error(
            "Max reconnection attempts reached. Please refresh the page."
          );
          setConnectionFailed(true);
          newSocket.disconnect();
        }
      });

      return () => {
        newSocket.close();
      };
    }
  }, [isAuthenticated, connectionFailed, reconnectAttempts]);

  const addResponse = useCallback((response: SocketResponse) => {
    setResponses((prevResponses) => ({
      ...prevResponses,
      [response.conversationId]: [
        ...(prevResponses[response.conversationId] || []),
        response,
      ],
    }));

    // Emit a custom event when a new response is received
    const event = new CustomEvent("socketResponse", { detail: response });
    window.dispatchEvent(event);
  }, []);

  const clearResponse = useCallback((conversationId: string) => {
    setResponses((prevResponses) => {
      const newResponses = { ...prevResponses };
      delete newResponses[conversationId];
      return newResponses;
    });
  }, []);

  useEffect(() => {
    if (socket) {
      if (!socket.connected && isAuthenticated && !connectionFailed) {
        console.log("Socket not connected, connecting");
        socket.connect();
      }

      socket.on("GWResponse", (response: SocketResponse) => {
        addResponse(response);
      });

      return () => {
        socket.off("GWResponse");
      };
    }
  }, [socket, addResponse, isAuthenticated, connectionFailed]);

  const addEventListener = useCallback(
    (eventName: string, callback: (event: CustomEvent) => void) => {
      window.addEventListener(eventName, callback as EventListener);
    },
    []
  );

  const removeEventListener = useCallback(
    (eventName: string, callback: (event: CustomEvent) => void) => {
      window.removeEventListener(eventName, callback as EventListener);
    },
    []
  );

  return (
    <SocketContext.Provider
      value={{
        socket,
        responses,
        clearResponse,
        connectionFailed,
        resetConnection,
        addEventListener,
        removeEventListener,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = (options?: {
  conversationId?: string;
}): SocketContextType => {
  const context = useContext(SocketContext);

  if (context === undefined) {
    throw new Error("useSocket must be used within a SocketProvider");
  }

  return context;
};
